import { OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import { http, jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';

import { ScheduleInstructionKind } from '#utils/instructions/shared/constants';
import type { AllFlagInstructionKinds } from '#utils/instructions/shared/types';
import type { ScheduledChange } from '#utils/scheduledChanges/records/ScheduledChange';
import { createScheduledChange } from '#utils/scheduledChanges/records/ScheduledChange';
import type { FlagConfigScheduledChangesState } from '#utils/scheduledChanges/types';

import type { UrlProps } from './types/utils';

const getScheduledChangesForFlag = ({ projKey, envKey, flagKey }: UrlProps) => {
  const url = `/api/v2/projects/${projKey}/flags/${flagKey}/environments/${envKey}/scheduled-changes`;
  return http
    .get(url, {
      beta: true,
    })
    .then(jsonToImmutable)
    .then((scheduledChanges) =>
      scheduledChanges
        .get('items')
        .map(createScheduledChange)
        .reduce(
          (accum: FlagConfigScheduledChangesState, curr: ScheduledChange) => accum.set(curr.get('_id'), curr),
          OrderedMap(),
        ),
    )
    .catch(jsonToImmutableError);
};

const getScheduledChangeForFlag = ({ projKey, envKey, flagKey, id }: UrlProps & { id: string }) => {
  const url = `/api/v2/projects/${projKey}/flags/${flagKey}/environments/${envKey}/scheduled-changes/${id}`;
  return http.get(url, { beta: true }).then(jsonToImmutable).then(createScheduledChange).catch(jsonToImmutableError);
};

const postScheduledChangesForFlag = ({
  projKey,
  envKey,
  flagKey,
  executionDate,
  instructions,
  comment,
}: {
  flagKey: string;
  projKey: string;
  envKey: string;
  executionDate: number;
  instructions: Array<{ kind: string }>;
  comment?: string;
}) => {
  const url = `/api/v2/projects/${projKey}/flags/${flagKey}/environments/${envKey}/scheduled-changes?ignoreConflicts=true`;
  return http
    .post(url, {
      beta: true,
      body: {
        executionDate,
        instructions,
        comment,
      },
    })
    .then(jsonToImmutable)
    .then(createScheduledChange)
    .catch(jsonToImmutableError);
};

const deleteScheduledChangesForFlag = ({ projKey, envKey, flagKey, workflowID }: UrlProps & { workflowID: string }) => {
  const url = `/api/v2/projects/${projKey}/flags/${flagKey}/environments/${envKey}/scheduled-changes/${workflowID}`;
  return http
    .delete(url, {
      beta: true,
    })
    .catch(jsonToImmutableError);
};

const updateScheduledChangesForFlag = ({
  projKey,
  envKey,
  flagKey,
  executionDate,
  workflowID,
  instructions,
  comment,
}: {
  flagKey: string;
  projKey: string;
  envKey: string;
  executionDate?: number;
  workflowID: string;
  instructions: Array<{ kind: AllFlagInstructionKinds }>;
  comment?: string;
}) => {
  const url = `/api/v2/projects/${projKey}/flags/${flagKey}/environments/${envKey}/scheduled-changes/${workflowID}?ignoreConflicts=true`;
  const bodyInstructions: Array<{ kind: AllFlagInstructionKinds | ScheduleInstructionKind; value: number | object }> = [
    { kind: ScheduleInstructionKind.REPLACE_SCHEDULED_CHANGES, value: instructions },
  ];
  const body = {
    comment,
    instructions: bodyInstructions,
  };

  if (executionDate) {
    body.instructions.push({
      kind: ScheduleInstructionKind.UPDATE_SCHEDULED_CHANGES_EXECUTION_DATE,
      value: executionDate,
    });
  }

  return http
    .patch(url, {
      beta: true,
      body,
    })
    .then(jsonToImmutable)
    .then(createScheduledChange)
    .catch(jsonToImmutableError);
};

export {
  getScheduledChangesForFlag,
  postScheduledChangesForFlag,
  deleteScheduledChangesForFlag,
  updateScheduledChangesForFlag,
  getScheduledChangeForFlag,
};
